/*
 * /#/stats — the read-only usage view.
 *
 * Answers three questions in order: how many people opened the page, how many
 * opened it in ChatGPT Desktop, and how many actually let the agent drive. The
 * numbers come from the stats action; nothing is computed here that the action
 * does not already say.
 *
 * The key is typed by the human and sent as a header. It is deliberately not in
 * the bundle, not in the URL (a query string ends up in logs, history and
 * referrers) and not in localStorage. sessionStorage keeps it for this tab only,
 * so a reload does not mean retyping it and closing the tab forgets it.
 *
 * There is no link to this route from anywhere in the app. That is not the
 * security boundary -- the key is -- but there is no reason to advertise it.
 */

import React, { useCallback, useEffect, useState } from 'react'

const ENDPOINT = '/api/v1/web/minipaint-webmcp/stats'
const KEY_STORAGE = 'minipaint.statsKey'

const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }
const card = {
  border: '1px solid #d3d3d3',
  borderRadius: 6,
  padding: '12px 16px',
  marginBottom: 16,
  background: '#fff'
}

/** sessionStorage throws in some privacy modes. A stats page is not worth a crash. */
function readStoredKey () {
  try {
    return window.sessionStorage.getItem(KEY_STORAGE) || ''
  } catch (e) {
    return ''
  }
}

function storeKey (value) {
  try {
    if (value) window.sessionStorage.setItem(KEY_STORAGE, value)
    else window.sessionStorage.removeItem(KEY_STORAGE)
  } catch (e) {
    /* not worth reporting */
  }
}

async function fetchStats (key) {
  const response = await fetch(ENDPOINT, {
    method: 'GET',
    headers: { 'X-Stats-Key': key },
    cache: 'no-store'
  })
  if (response.status === 401) {
    const err = new Error('That key was not accepted.')
    err.unauthorised = true
    throw err
  }
  if (!response.ok) throw new Error(`The stats action answered with status ${response.status}.`)
  return response.json()
}

function Figure ({ label, value, note }) {
  return (
    <div style={{ flex: '1 1 160px' }}>
      <div style={{ fontSize: 34, fontWeight: 700, lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4 }}>{label}</div>
      {note ? <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{note}</div> : null}
    </div>
  )
}

function CountTable ({ title, counts, empty }) {
  const rows = Object.entries(counts || {}).sort((a, b) => b[1] - a[1])
  return (
    <div style={card}>
      <h2 style={{ margin: '0 0 8px', fontSize: 15 }}>{title}</h2>
      {rows.length === 0
        ? <div style={{ color: '#666', fontSize: 13 }}>{empty}</div>
        : (
          <table style={{ ...mono, borderCollapse: 'collapse', width: '100%' }}>
            <tbody>
              {rows.map(([name, count]) => (
                <tr key={name} style={{ borderTop: '1px solid #eee' }}>
                  <td style={{ padding: '4px 0' }}>{name}</td>
                  <td style={{ padding: '4px 0', textAlign: 'right' }}>{count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
    </div>
  )
}

/*
 * Timestamps are stored as UTC, which is right for storage and wrong for reading.
 * They are rendered in the viewer's own timezone, named in the column header so
 * the number is never ambiguous. The day buckets stay UTC: a day boundary has to
 * be fixed somewhere or the counts would shift with whoever is looking.
 */
const TIME_ZONE = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'local time'
  } catch (e) {
    return 'local time'
  }
})()

function localTime (iso) {
  const at = new Date(iso)
  if (!iso || Number.isNaN(at.getTime())) return ''
  try {
    return at.toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  } catch (e) {
    return String(iso).replace('T', ' ').slice(0, 19)
  }
}

function toolSummary (tools) {
  return Object.entries(tools || {})
    .sort((a, b) => b[1] - a[1])
    .map(([name, count]) => `${name}×${count}`)
    .join('  ')
}

export const Stats = () => {
  const [key, setKey] = useState(readStoredKey)
  const [typed, setTyped] = useState('')
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async (withKey) => {
    if (!withKey) return
    setBusy(true)
    setError(null)
    try {
      const body = await fetchStats(withKey)
      setData(body)
      storeKey(withKey)
    } catch (e) {
      setError(e.message)
      setData(null)
      if (e.unauthorised) {
        // A rejected key is not worth keeping around for the next reload.
        storeKey('')
        setKey('')
      }
    } finally {
      setBusy(false)
    }
  }, [])

  useEffect(() => { if (key) load(key) }, [key, load])

  const signOut = () => {
    storeKey('')
    setKey('')
    setData(null)
    setTyped('')
    setError(null)
  }

  if (!key) {
    return (
      <div style={{ padding: 24, maxWidth: 420, fontFamily: 'system-ui, sans-serif' }}>
        <h1 style={{ fontSize: 20, margin: '0 0 4px' }}>Usage</h1>
        <p style={{ color: '#666', fontSize: 13, marginTop: 0 }}>
          Enter the stats key. It is kept for this browser tab only.
        </p>
        <form onSubmit={(e) => { e.preventDefault(); setKey(typed.trim()) }}>
          <input
            type='password'
            value={typed}
            autoFocus
            autoComplete='off'
            onChange={(e) => setTyped(e.target.value)}
            placeholder='Stats key'
            style={{ ...mono, width: '100%', padding: 8, boxSizing: 'border-box' }}
          />
          <button
            type='submit'
            disabled={!typed.trim()}
            style={{ marginTop: 8, padding: '6px 14px', cursor: 'pointer' }}
          >
            Show
          </button>
        </form>
        {error ? <p style={{ color: '#991b1b', fontSize: 13 }}>{error}</p> : null}
      </div>
    )
  }

  const funnel = (data && data.funnel) || {}

  return (
    <div style={{ padding: 24, maxWidth: 900, fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 16 }}>
        <h1 style={{ fontSize: 20, margin: 0 }}>Usage</h1>
        <button onClick={() => load(key)} disabled={busy} style={{ cursor: 'pointer' }}>
          {busy ? 'Loading…' : 'Refresh'}
        </button>
        <button onClick={signOut} style={{ cursor: 'pointer' }}>Forget key</button>
      </div>

      {error ? <div style={{ ...card, color: '#991b1b' }}>{error}</div> : null}
      {!data && !error ? <div style={{ color: '#666' }}>Loading…</div> : null}

      {data
        ? (
          <>
            <div style={{ ...card, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <Figure label='Visits' value={funnel.visits ?? 0} note='page opened' />
              <Figure
                label='In ChatGPT Desktop'
                value={funnel.openedInChatGptDesktop ?? 0}
                note={`${funnel.percentOpenedInChatGpt ?? 0}% of visits · native WebMCP`}
              />
              <Figure
                label='Let the agent drive'
                value={funnel.usedTheAgent ?? 0}
                note={`${funnel.percentUsedTheAgent ?? 0}% of visits · called a tool`}
              />
              <Figure label='Tool calls' value={data.toolCalls ?? 0} note='by agents' />
            </div>

            <CountTable title='Tools called' counts={data.byTool} empty='No agent has called a tool yet.' />

            <div style={card}>
              <h2 style={{ margin: '0 0 8px', fontSize: 15 }}>Recent sessions</h2>
              <p style={{ color: '#666', fontSize: 12, marginTop: 0 }}>
                One row per page load that used tools. Nothing identifies the visitor:
                each row is a page load and cannot be joined to anything before or after it.
              </p>
              {(data.recentSessions || []).length === 0
                ? <div style={{ color: '#666', fontSize: 13 }}>No sessions recorded yet.</div>
                : (
                  <table style={{ ...mono, borderCollapse: 'collapse', width: '100%' }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: '#666' }}>
                        <th style={{ padding: '4px 8px 4px 0', fontWeight: 600 }}>When ({TIME_ZONE})</th>
                        <th style={{ padding: '4px 8px 4px 0', fontWeight: 600 }}>Browser</th>
                        <th style={{ padding: '4px 8px 4px 0', fontWeight: 600 }}>From</th>
                        <th style={{ padding: '4px 8px 4px 0', fontWeight: 600, textAlign: 'right' }}>Calls</th>
                        <th style={{ padding: '4px 0', fontWeight: 600 }}>Tools</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recentSessions.map((s, i) => (
                        <tr key={i} style={{ borderTop: '1px solid #eee', verticalAlign: 'top' }}>
                          <td style={{ padding: '4px 8px 4px 0', whiteSpace: 'nowrap' }}>
                            {localTime(s.at)}
                          </td>
                          <td style={{ padding: '4px 8px 4px 0' }}>
                            {s.transport === 'native' ? 'ChatGPT Desktop' : s.transport}
                          </td>
                          <td style={{ padding: '4px 8px 4px 0' }}>{s.source}</td>
                          <td style={{ padding: '4px 8px 4px 0', textAlign: 'right' }}>{s.toolCalls}</td>
                          <td style={{ padding: '4px 0' }}>{toolSummary(s.tools)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  )}
            </div>

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 240px' }}>
                <CountTable title='Browser' counts={data.byBrowser} empty='No visits yet.' />
              </div>
              <div style={{ flex: '1 1 240px' }}>
                <CountTable title='Referrer' counts={data.bySource} empty='No visits yet.' />
              </div>
            </div>

            <div style={card}>
              <h2 style={{ margin: '0 0 8px', fontSize: 15 }}>By day</h2>
              <table style={{ ...mono, borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#666' }}>
                    <th style={{ padding: '4px 8px 4px 0', fontWeight: 600 }}>Day (UTC)</th>
                    <th style={{ padding: '4px 8px 4px 0', fontWeight: 600, textAlign: 'right' }}>Visits</th>
                    <th style={{ padding: '4px 8px 4px 0', fontWeight: 600, textAlign: 'right' }}>ChatGPT</th>
                    <th style={{ padding: '4px 8px 4px 0', fontWeight: 600, textAlign: 'right' }}>Agent used</th>
                    <th style={{ padding: '4px 0', fontWeight: 600, textAlign: 'right' }}>Tool calls</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.byDay || []).map((d) => (
                    <tr key={d.day} style={{ borderTop: '1px solid #eee' }}>
                      <td style={{ padding: '4px 8px 4px 0' }}>{d.day}</td>
                      <td style={{ padding: '4px 8px 4px 0', textAlign: 'right' }}>{d.visits}</td>
                      <td style={{ padding: '4px 8px 4px 0', textAlign: 'right' }}>{d.openedInChatGptDesktop}</td>
                      <td style={{ padding: '4px 8px 4px 0', textAlign: 'right' }}>{d.usedTheAgent}</td>
                      <td style={{ padding: '4px 0', textAlign: 'right' }}>{d.toolCalls}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p style={{ color: '#666', fontSize: 12 }}>
              The beacon endpoint is public, because the caller is a browser with no token.
              Anyone willing to call it can inflate these counts. Read them as a trend.
            </p>
          </>
          )
        : null}
    </div>
  )
}
